/* eslint-disable react/prop-types */
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  FormGroup,
  FormControlLabel,
  Switch,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { getRequestorDetails, updateRequestor } from 'src/redux/requestorSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import PasswordResetModal from 'src/common/PasswordResetModal';
import PropTypes from 'prop-types';
import NewRequestor from './NewRequestor';

/* ---------- Info Block with Smart Tooltip ---------- */
function InfoBlock({ label, value }) {
  const theme = useTheme();
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setShowTooltip(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    }
  }, [value]);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5
        }}
      >
        {label}
      </Typography>

      <Tooltip title={showTooltip ? value : ''} arrow>
        <Typography
          ref={textRef}
          variant="body1"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {value ?? '--'}
        </Typography>
      </Tooltip>
    </Box>
  );
}

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : '--';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

function RequestorDetails() {
  const theme = useTheme();
  const { userId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, requestorDetails } = useSelector(
    (state) => state.requestors
  );

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassResetModal, setShowPassResetModal] = useState(false);

  useEffect(() => {
    dispatch(getRequestorDetails(userId)).unwrap();
  }, [dispatch, userId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const values = {
        ...requestorDetails,
        is_active: !requestorDetails.is_active
      };

      await dispatch(updateRequestor(values)).unwrap();

      dispatch(
        setSuccessInfo({
          successMsg: 'Requestor status updated successfully!'
        })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error?.error_message }));
    }

    setIsLoading(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewRequestor
        open={openModal}
        initialValues={requestorDetails}
        handleClose={() => setOpenModal(false)}
      />

      {loading || isLoading ? (
        <Loader />
      ) : (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 140px)'
          }}
        >
          <CardHeader
            sx={{
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bgcolor: 'background.paper'
            }}
            title={(
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  REQUESTOR DETAILS
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage requestor information
                </Typography>
              </Stack>
            )}
            action={(
              <Button
                variant="outlined"
                startIcon={<ArrowBackIosNewIcon />}
                onClick={() => history.replace(ROUTE.requestors)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main
                  }
                }}
              >
                Back
              </Button>
            )}
          />

          <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
            <Grid container spacing={3}>
              {Object.entries(requestorDetails || {}).map(([key, value]) => {
                if (key === 'is_active') {
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ textTransform: 'uppercase' }}
                        >
                          Status
                        </Typography>

                        <FormGroup>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(value)}
                                onChange={() => setShowConfirmModal(true)}
                                color="success"
                              />
                            )}
                            label={value ? 'Active' : 'Inactive'}
                          />
                        </FormGroup>
                      </Box>
                    </Grid>
                  );
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <InfoBlock
                      label={formatLabel(key)}
                      value={formatValue(value)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>

          <Divider />

          <CardActions sx={{
            p: 2.5, justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
          }}
          >
            <Button
              variant="contained"
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                px: 4
              }}
            >
              Update Details
            </Button>

            <Button
              variant="contained"
              onClick={() => setShowPassResetModal(true)}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Reset Password
            </Button>
          </CardActions>
        </Card>
      )}

      <PasswordResetModal
        open={showPassResetModal}
        userEmail={requestorDetails?.email}
        onClose={() => setShowPassResetModal(false)}
      />

      <ConfirmationDialog
        info={
          requestorDetails.is_active
            ? CONFIRMATION_MSG.disableRequestor
            : CONFIRMATION_MSG.enableRequestor
        }
        open={showConfirmModal}
        disable={requestorDetails.is_active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

InfoBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any
};

export default RequestorDetails;
